import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type { BadgeTone } from "@empite/ui-components";
import type {
  UsageDrift,
  UsageDriftDimension,
  UsageDriftQuery,
  UsageDriftStatus,
} from "../admin-client";
import { useAnalyticsDrift } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { NotPermitted } from "./packages";
import { Pagination, ShortID, pageOf, usePaging, when } from "./common";
import { MONEY, READ, WITHHELD, money } from "./analytics-common";

/**
 * `/analytics/drift` — where the PROVIDER's own accounting disagrees with ours.
 *
 * One row is one bucket: a provider account, a model, and a UTC day. The worker
 * sweep fetches what the vendor published for that bucket, sets it beside what
 * we billed, and files a verdict. This screen is the operator's window onto
 * those verdicts and onto nothing else.
 *
 * IT CORRECTS NOTHING, and it says so above the table in the server's own
 * words. A drift record is EVIDENCE FOR A HUMAN: settlement is final, and no
 * settled request is ever repriced because a provider's report disagreed with
 * it a day later. That is why there is no ReasonButton anywhere below — a
 * reason code is the ceremony that precedes a MUTATION, and there is no
 * mutation on this surface to precede. The Admin API has no writing
 * counterpart to this route, and migration 0067 grants the service SELECT only.
 *
 * THE THREE STATUSES ARE THREE FACTS, NOT TWO. `agrees` and `drifted` are both
 * verdicts on a comparison; `not_reported` means the provider published nothing
 * at all, so there was no comparison to reach a verdict on. Drawing that third
 * one like the first would report SILENCE AS CONFIRMATION, which is the single
 * most dangerous thing this screen could say — a feed that has quietly stopped
 * would look exactly like a provider who agrees with every figure we have. So
 * it gets its own tone, its own word, and a line under it saying what it means.
 *
 * For the same reason the default filter shows EVERYTHING rather than only the
 * drifted rows: a screen that only ever shows disagreement cannot show an
 * operator that a provider has gone quiet.
 *
 * Behind `analytics:read`, the same grant the rest of the analytics surface
 * takes — nothing in the document names a person, so `users:read` buys nothing
 * here. The three cost columns need `wallets:read` on top and come back ABSENT
 * rather than zeroed without it, which is a decision only the server makes.
 */

/** How many rows to ask the server for. The route clamps at 500, so this is the
 *  whole page it will give — the paging below is a window onto rows the browser
 *  already holds, exactly as `/alerts` pages its crossings. */
const DRIFT_LIMIT = 500;
const DRIFT_PER_PAGE = 25;

export interface DriftSearch {
  from?: string;
  to?: string;
  provider?: string;
  /** The string "true", or absent. `?drifted=false` and no parameter are the
   *  same question, so only the narrowing value is ever written. */
  drifted?: string;
}

/** The unfiltered question, spelled out so a link can ask it without every call
 *  site listing the parameters it is not sending. */
export const NO_DRIFT_FILTER: DriftSearch = {
  from: undefined,
  to: undefined,
  provider: undefined,
  drifted: undefined,
};

export function Drift({ search }: { search: DriftSearch }) {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, READ) ? (
          <DriftScreen search={search} />
        ) : (
          <NotPermitted title="Usage drift" permission={READ} />
        )
      }
    </RequireOperator>
  );
}

function DriftScreen({ search }: { search: DriftSearch }) {
  const navigate = useNavigate();

  // The form's own state is what the operator is TYPING; the URL is what has
  // been asked for. The same split every filtered screen here makes, for the
  // same reason: a control that navigated on change would put every half-built
  // filter in the back button's history.
  const [from, setFrom] = useState(search.from ?? "");
  const [to, setTo] = useState(search.to ?? "");
  const [provider, setProvider] = useState(search.provider ?? "");
  const [drifted, setDrifted] = useState(search.drifted === "true" ? "true" : "");

  // The URL is the authority. The back button and a pasted link both move it
  // without the form knowing, and a form still showing the previous filter is a
  // control that lies about what is on screen.
  useEffect(() => {
    setFrom(search.from ?? "");
    setTo(search.to ?? "");
    setProvider(search.provider ?? "");
    setDrifted(search.drifted === "true" ? "true" : "");
  }, [search.from, search.to, search.provider, search.drifted]);

  // Which bucket is opened up. A set rather than one at a time, unlike
  // `/alerts`: the detail here is an inline five-row table under the row it
  // belongs to, so two open buckets sit one above the other instead of
  // replacing each other — and comparing two days of one model is the whole
  // reason somebody opens a second.
  const [open, setOpen] = useState<Record<string, boolean>>({});

  // Nothing is narrowed on the way out. The dates are refused by the Admin API
  // with a 400 naming the form it wants, and `provider` is an OPEN vocabulary —
  // the column carries whatever the routing catalogue named — so there is no
  // client-side list to check it against.
  const query: UsageDriftQuery = {
    from: search.from,
    to: search.to,
    provider: search.provider,
    drifted: search.drifted === "true",
    limit: DRIFT_LIMIT,
  };
  const report = useAnalyticsDrift(query);
  const rows = report.data?.drift ?? [];
  const moneyVisible = report.data?.money_visible ?? false;

  // Above the early returns, on the rule `usePaging` documents: a hook called
  // below one runs on some renders and not others.
  const paging = usePaging(DRIFT_PER_PAGE);
  const paged = pageOf(rows, paging);

  const driftedOnly = report.data?.drifted_only ?? false;
  const narrowed = Boolean(search.from || search.to || search.provider || driftedOnly);

  const go = (next: DriftSearch) => {
    setOpen({});
    void navigate({ to: "/analytics/drift", search: { ...NO_DRIFT_FILTER, ...next } });
  };

  return (
    <>
      <PageHeader
        title="Usage drift"
        description="What each provider said about a day's tokens, beside what we billed for it. One row is one provider account, one model and one UTC day. Our side sums EVERY attempt's usage record — not the per-model dashboard figure, which keeps only the attempt that decided a request."
      />

      {/* The server's own sentence, printed rather than paraphrased. It is the
          first thing on the page because it governs how every number under it
          should be acted on: these are observations to investigate, not
          balances to correct. */}
      <Alert tone="info" title="This screen corrects nothing">
        {report.data?.note ??
          "A drift record is evidence for a human, not a correction. Settlement is final: no settled request is ever repriced because a provider's report disagreed with it afterwards."}
      </Alert>

      <Card title="Filter">
        <form
          className="esm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            go({
              from: from || undefined,
              to: to || undefined,
              provider: provider.trim() || undefined,
              // The default is dropped rather than written: `?drifted=false`
              // and no parameter at all are the same question.
              drifted: drifted === "true" ? "true" : undefined,
            });
          }}
        >
          <Field label="From" htmlFor="drift-from" hint="A UTC day, inclusive.">
            <Input
              id="drift-from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </Field>
          <Field label="To" htmlFor="drift-to" hint="A UTC day, inclusive.">
            <Input
              id="drift-to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </Field>
          <Field
            label="Provider"
            htmlFor="drift-provider"
            hint="As the routing catalogue names it — openai, anthropic."
          >
            <Input
              id="drift-provider"
              value={provider}
              onChange={(event) => setProvider(event.target.value)}
              placeholder="Any provider"
            />
          </Field>
          <Field
            label="Show"
            htmlFor="drift-drifted"
            // Said at the control, because this is the one filter on the page
            // that can turn a broken feed into a clean bill of health.
            hint="Every bucket is the honest default: a provider that has stopped reporting only shows up in the rows that are not drifted."
          >
            <Select
              id="drift-drifted"
              value={drifted}
              onChange={(event) => setDrifted(event.target.value)}
            >
              <option value="">Every bucket</option>
              <option value="true">Drifted only</option>
            </Select>
          </Field>
          <Button type="submit" busy={report.isFetching}>
            Apply
          </Button>
        </form>
      </Card>

      <ErrorAlert error={report.error} title="Could not load the drift records" />

      <Card title={driftedOnly ? "Buckets that drifted" : "Every compared bucket"}>
        {report.isPending ? (
          <Spinner label="Loading the drift records" />
        ) : rows.length === 0 ? (
          // "Nothing has been compared" is a claim about the PLATFORM, so it is
          // only made when the filter narrows nothing. Under one, the same
          // empty table says what was asked instead — and it never says "every
          // provider agrees", because an empty set of verdicts is the absence
          // of a comparison rather than the result of one.
          <EmptyState title={narrowed ? "Nothing matches that filter" : "Nothing has been compared"}>
            {narrowed
              ? "No bucket matches. Widen the filter before reading this as agreement — 'drifted only' in particular hides every bucket where the provider reported nothing at all."
              : "The reconciliation sweep has filed no verdicts yet. That is not the same as every provider agreeing with us; it means there is nothing to compare."}
          </EmptyState>
        ) : (
          <>
            <DataTable<UsageDrift>
              rows={paged.visible}
              rowKey={(row) => row.id}
              empty="No bucket matches that filter."
              columns={[
                { key: "day", header: "Day", render: (row) => row.day },
                {
                  key: "provider",
                  header: "Provider",
                  render: (row) => (
                    <>
                      <code>{row.provider}</code>
                      {/* WHICH account. A tenant routinely holds several with
                          one provider, and "openai drifted" is not something
                          anybody can act on when three accounts could have. */}
                      <div className="esm-muted-text">
                        {row.account.label} · {row.account.environment}
                      </div>
                    </>
                  ),
                },
                {
                  key: "model",
                  header: "Model",
                  // As the PROVIDER names it, never a client's alias: an alias
                  // is our vocabulary and the vendor has never heard of it.
                  render: (row) => <code>{row.model}</code>,
                },
                {
                  key: "status",
                  header: "Status",
                  render: (row) => <DriftStatusBadge status={row.status} />,
                },
                {
                  key: "worst",
                  header: "Worst drift",
                  align: "end",
                  // The ratio and the magnitude together, because either alone
                  // misleads: a 100% drift on three tokens is noise, and a 1%
                  // drift on forty million is not.
                  render: (row) => (
                    <>
                      <div className="esm-amount">{bps(row.worst_drift_bps)}</div>
                      <div className="esm-muted-text">{magnitude(row.worst_drift_abs)}</div>
                    </>
                  ),
                },
                {
                  key: "requests",
                  header: "Requests",
                  align: "end",
                  render: (row) => (
                    <Comparison local={row.local_requests} provider={row.provider_requests} />
                  ),
                },
                {
                  key: "cost",
                  header: "Cost",
                  align: "end",
                  render: (row) =>
                    moneyVisible ? (
                      <>
                        <div className="esm-amount">
                          {money(row.local_cost_micros, true, undefined) ?? "—"}
                        </div>
                        <div className="esm-muted-text">
                          {row.provider_cost_micros === null ||
                          row.provider_cost_micros === undefined
                            ? NOT_REPORTED
                            : `provider ${money(row.provider_cost_micros, true, undefined)}`}
                        </div>
                      </>
                    ) : (
                      // Absent, not zero. Named so an operator asks for the
                      // grant rather than raising a bug against a blank column.
                      <span className="esm-muted-text" title={WITHHELD}>
                        needs {MONEY}
                      </span>
                    ),
                },
                {
                  key: "raised",
                  header: "Raised",
                  // The trail to what was DONE about it. Empty for `agrees`,
                  // for `not_reported`, and for a difference inside the
                  // alerting bounds — an absence means nobody was woken, not
                  // that nobody looked.
                  render: (row) =>
                    row.alert_id || row.failure_id ? (
                      <>
                        <ShortID value={row.alert_id ?? undefined} />
                        {row.failure_id ? (
                          <div className="esm-muted-text">
                            failure <ShortID value={row.failure_id} />
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <span className="esm-muted-text">nobody woken</span>
                    ),
                },
                {
                  key: "computed",
                  header: "Computed",
                  render: (row) => when(row.computed_at),
                },
                {
                  key: "open",
                  header: <span className="esm-visually-hidden">Dimensions</span>,
                  render: (row) => (
                    <button
                      type="button"
                      className="esm-disclose"
                      aria-expanded={Boolean(open[row.id])}
                      onClick={() => setOpen({ ...open, [row.id]: !open[row.id] })}
                    >
                      {open[row.id] ? "Hide" : "Open"}
                      <span className="esm-visually-hidden"> the five dimensions of this bucket</span>
                    </button>
                  ),
                },
              ]}
            />
            <Pagination paged={paged} noun="buckets" />
          </>
        )}
      </Card>

      {/* The opened buckets, in the order the table shows them. Below the table
          rather than inside it: a five-row table nested in a cell of another
          table is a shape no screen reader reads back usefully. */}
      {paged.visible
        .filter((row) => open[row.id])
        .map((row) => (
          <DriftDimensions key={row.id} row={row} />
        ))}
    </>
  );
}

/**
 * The bucket's verdict, drawn so that SILENCE CANNOT BE MISTAKEN FOR
 * AGREEMENT.
 *
 * Three tones and three words for three facts. `not_reported` is deliberately
 * not neutral-grey and deliberately not called "no drift": the provider
 * published nothing, the row carries no comparison at all, and an operator
 * skimming the column has to be able to see that the number they were looking
 * for is missing rather than reassuring.
 */
function DriftStatusBadge({ status }: { status: UsageDriftStatus }) {
  const tone: BadgeTone =
    status === "drifted" ? "danger" : status === "agrees" ? "success" : "warning";
  return (
    <>
      <Badge tone={tone}>{DRIFT_STATUS_WORD[status] ?? status}</Badge>
      {status === "not_reported" ? (
        <div className="esm-muted-text">the provider published nothing</div>
      ) : null}
    </>
  );
}

/** The word on the pill. `not_reported` gets a sentence-shaped one, because the
 *  underscore spelling reads as a machine state and this one is a fact about
 *  the world. */
const DRIFT_STATUS_WORD: Record<string, string> = {
  agrees: "agrees",
  drifted: "drifted",
  not_reported: "not reported",
};

/** What an absent provider figure says. Never "—" and never 0: a dash reads as
 *  "this console failed to load a number" and a zero reads as "they counted
 *  nothing", and the truth is that they said nothing at all. */
const NOT_REPORTED = "not reported";

/**
 * The five canonical dimensions of one bucket, ours beside theirs.
 *
 * In the order a bill prices them — input, cached input, cache write, output,
 * reasoning — which the server fixes, so a verdict and an invoice read down the
 * same list. `total` is not among them and must never be: a total that drifts
 * is just the dimensions that drifted, counted a second time.
 */
function DriftDimensions({ row }: { row: UsageDrift }) {
  return (
    <Card
      title={`${row.provider} · ${row.model} · ${row.day}`}
      actions={<DriftStatusBadge status={row.status} />}
    >
      <DataTable<UsageDriftDimension>
        rows={row.dimensions}
        rowKey={(dim) => dim.dimension}
        empty="No dimension rows were filed for this bucket."
        columns={[
          {
            key: "dimension",
            header: "Dimension",
            render: (dim) => <code>{dim.dimension}</code>,
          },
          {
            key: "local",
            header: "We billed",
            align: "end",
            // Always known. We billed something, even when that something was
            // zero, so this column never says "not reported".
            render: (dim) => dim.local_tokens.toLocaleString(),
          },
          {
            key: "provider",
            header: "Provider reported",
            align: "end",
            render: (dim) =>
              dim.reported ? (
                (dim.provider_tokens ?? 0).toLocaleString()
              ) : (
                <span className="esm-muted-text">{NOT_REPORTED}</span>
              ),
          },
          {
            key: "abs",
            header: "Difference",
            align: "end",
            // Signed, and the sign is the point: positive means the provider
            // counted MORE than we billed, which is the direction that costs
            // the platform money.
            render: (dim) => tokens(dim.drift_abs),
          },
          {
            key: "bps",
            header: "As a ratio",
            align: "end",
            render: (dim) => bps(dim.drift_bps),
          },
        ]}
      />
    </Card>
  );
}

/** Our figure over theirs, in one cell. Theirs is absent far more often than
 *  not, and the absence is drawn as words rather than as a blank. */
function Comparison({ local, provider }: { local: number; provider: number | null }) {
  return (
    <>
      <div className="esm-amount">{local.toLocaleString()}</div>
      <div className="esm-muted-text">
        {provider === null ? NOT_REPORTED : `provider ${provider.toLocaleString()}`}
      </div>
    </>
  );
}

/**
 * The bucket's headline magnitude, UNSIGNED — which is what the column stores
 * and what a threshold is compared against. The direction lives on the
 * dimension rows below, where there is one difference rather than the worst of
 * five, so a sign here would be attributing a direction to a maximum.
 *
 * Null is the `not_reported` case: there is no comparison on the row at all.
 */
function magnitude(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return NOT_REPORTED;
  }
  return `${value.toLocaleString()} tokens`;
}

/**
 * A signed token difference.
 *
 * A null is NOT a zero — either the provider reported nothing, or there is no
 * comparison on this row at all — so it prints the words. The sign is kept on
 * a positive number, because "+120,000" and "120,000" are the same magnitude
 * pointing opposite ways and the column mixes both.
 */
function tokens(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return NOT_REPORTED;
  }
  return `${value > 0 ? "+" : ""}${value.toLocaleString()} tokens`;
}

/**
 * A drift in basis points, rendered as a percentage.
 *
 * Null is NOT zero and says so: the ratio is undefined whenever OUR figure is
 * zero — there is no denominator — which is exactly why the absolute figure
 * sits beside it. Divided by 100 rather than by 10000 because the result is
 * printed with a `%`; 10000 bps is 100%.
 */
function bps(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "no ratio";
  }
  return `${value > 0 ? "+" : ""}${(value / 100).toFixed(2)}%`;
}
